// ElevenLabs Agents (Qwen-powered) voice driver for Call God.
//
// Mirrors the useRealtimeVoice contract so Call.jsx can swap providers:
//   { sessionId, pttDown, pttUp, cancelAssistant, endCall, audioBlocked, unblockAudio }
//
// Connection: backend mints a WebRTC conversation token (/api/eleven/token);
// the ElevenLabs SDK handles transport + audio. Client tools (remember_name,
// seek_wisdom, flag_safety_event, finish_session) run here against our API.
// If connection fails BEFORE it is established, onFallback() lets the page
// retry the same call on the OpenAI path.

import { useCallback, useEffect, useRef, useState } from "react";
import { useConversation } from "@elevenlabs/react";

import { getDeviceId } from "@/lib/device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function sid() {
  return "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export function useElevenVoice({
  enabled,
  mode,
  muted,
  callState,
  dispatch,
  persona = "god",
  onFallback,
}) {
  const [sessionId] = useState(() => sid());
  const sessionIdRef = useRef(sessionId);
  const connectedRef = useRef(false);
  const startingRef = useRef(false);
  const startedAtRef = useRef(null);
  const respCounterRef = useRef(0);
  const pttHeldRef = useRef(false);

  const modeRef = useRef(mode);
  const mutedRef = useRef(muted);
  const dispatchRef = useRef(dispatch);
  const onFallbackRef = useRef(onFallback);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { dispatchRef.current = dispatch; }, [dispatch]);
  useEffect(() => { onFallbackRef.current = onFallback; }, [onFallback]);

  const conversation = useConversation();
  const convRef = useRef(conversation);
  useEffect(() => { convRef.current = conversation; });

  const dispatchSafe = useCallback((action) => {
    const fn = dispatchRef.current;
    if (typeof fn === "function") fn(action);
  }, []);

  const applyMicForMode = useCallback(() => {
    const c = convRef.current;
    if (!c || !connectedRef.current) return;
    try {
      if (modeRef.current === "ptt") {
        c.setMuted(!pttHeldRef.current || mutedRef.current);
      } else {
        c.setMuted(mutedRef.current);
      }
    } catch (_) { /* not connected yet */ }
  }, []);

  // ---------- client tools ----------
  const clientToolsRef = useRef(null);
  if (!clientToolsRef.current) {
    clientToolsRef.current = {
      remember_name: async ({ name }) => {
        const cleanName = String(name || "").trim().slice(0, 40);
        if (cleanName) {
          try {
            await fetch(`${API}/soul`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "x-device-id": getDeviceId(),
              },
              body: JSON.stringify({ name: cleanName, device_id: getDeviceId() }),
            });
          } catch (_) { /* never break the call over a write */ }
        }
        return "remembered";
      },
      seek_wisdom: async ({ topic, mood }) => {
        try {
          const res = await fetch(`${API}/wisdom/search`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              topic: String(topic || "").slice(0, 300),
              mood: String(mood || "").slice(0, 100),
              limit: 3,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            return JSON.stringify({ passages: data.passages || [] });
          }
        } catch (_) { /* speak plainly without passages */ }
        return JSON.stringify({ passages: [] });
      },
      flag_safety_event: async ({ category, severity, summary }) => {
        dispatchRef.current?.({
          type: "SAFETY_EVENT",
          category: category || "other",
          severity: severity || "high",
          summary: summary || "",
        });
        try {
          await fetch(`${API}/sessions/${sessionIdRef.current}/safety_event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ category, severity, summary: summary || "" }),
          });
        } catch (_) { /* ignore */ }
        return "logged";
      },
      finish_session: async () => {
        setTimeout(() => dispatchRef.current?.({ type: "END" }), 2500);
        return "ending";
      },
    };
  }

  // ---------- connect effect ----------
  useEffect(() => {
    if (!enabled) return undefined;
    if (callState !== "connecting") return undefined;
    if (connectedRef.current || startingRef.current) return undefined;
    startingRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        // 1) Mic permission up-front so denial maps to the right FSM state.
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
        } catch (e) {
          if (!cancelled) dispatchSafe({ type: "MIC_DENIED" });
          return;
        }
        if (cancelled) return;

        // 2) Token from our backend (key stays server-side).
        const res = await fetch(
          `${API}/eleven/token?persona=${encodeURIComponent(persona)}&device_id=${encodeURIComponent(getDeviceId())}`,
        );
        if (!res.ok) {
          let code = "";
          try { code = (await res.json())?.error || ""; } catch (_) { /* not json */ }
          if (code === "eleven_busy") {
            startingRef.current = false;
            dispatchSafe({
              type: "REALTIME_ERROR",
              message: "The line is busy right now. Please try again in a moment.",
            });
            return;
          }
          throw new Error(`eleven_token_${res.status}`);
        }
        const data = await res.json();
        if (cancelled) return;

        // 3) Start the ElevenLabs session (WebRTC).
        convRef.current.startSession({
          conversationToken: data.token,
          connectionType: "webrtc",
          dynamicVariables: { user_name: data.user_name || "unknown" },
          overrides: { agent: { firstMessage: data.first_message } },
          clientTools: clientToolsRef.current,
          onConnect: () => {
            connectedRef.current = true;
            startedAtRef.current = Date.now();
            dispatchSafe({ type: "MIC_GRANTED" });
            dispatchSafe({ type: "REALTIME_CONNECTED" });
            applyMicForMode();
            try {
              fetch(`${API}/sessions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  session_id: sessionIdRef.current,
                  device_id: getDeviceId(),
                  mode: modeRef.current,
                }),
              });
            } catch (_) { /* non-blocking */ }
          },
          onMessage: ({ message, source }) => {
            if (!message) return;
            if (source === "user") {
              dispatchSafe({ type: "TRANSCRIPT_USER_LINE", text: message });
            } else {
              respCounterRef.current += 1;
              dispatchSafe({
                type: "TRANSCRIPT_ASSISTANT_FINAL",
                responseId: `el-${respCounterRef.current}`,
                text: message,
              });
            }
          },
          onModeChange: ({ mode: m }) => {
            if (m === "speaking") dispatchSafe({ type: "ASSISTANT_STARTED" });
            else dispatchSafe({ type: "ASSISTANT_STOPPED" });
          },
          onDisconnect: (details) => {
            const wasConnected = connectedRef.current;
            connectedRef.current = false;
            startingRef.current = false;
            if (details?.reason === "user") return;
            if (wasConnected) {
              dispatchSafe({
                type: "REALTIME_ERROR",
                message: "The line dropped. Please try again.",
              });
            }
          },
          onError: (message) => {
            console.warn("[cg.eleven.error]", message);
            if (!connectedRef.current) {
              startingRef.current = false;
              if (onFallbackRef.current) onFallbackRef.current();
              else dispatchSafe({ type: "REALTIME_ERROR", message: "The voice is quiet right now. Please try again soon." });
            }
          },
        });
      } catch (e) {
        console.warn("[cg.eleven] connect failed:", e?.message || e);
        startingRef.current = false;
        if (cancelled) return;
        if (onFallbackRef.current) onFallbackRef.current();
        else {
          dispatchSafe({
            type: "REALTIME_ERROR",
            message: "The voice is quiet right now. Please try again soon.",
          });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [enabled, callState, persona, dispatchSafe, applyMicForMode]);

  // ---------- teardown when disabled / unmount ----------
  useEffect(() => {
    if (enabled) return undefined;
    if (connectedRef.current) {
      try { convRef.current.endSession(); } catch (_) { /* ignore */ }
      connectedRef.current = false;
      startingRef.current = false;
    }
    return undefined;
  }, [enabled]);

  useEffect(() => () => {
    if (connectedRef.current) {
      try { convRef.current.endSession(); } catch (_) { /* ignore */ }
      connectedRef.current = false;
    }
  }, []);

  // Mode / mute sync
  useEffect(() => { applyMicForMode(); }, [mode, muted, applyMicForMode]);

  // ---------- controls (same contract as useRealtimeVoice) ----------
  const pttDown = useCallback(() => {
    pttHeldRef.current = true;
    applyMicForMode();
  }, [applyMicForMode]);

  const pttUp = useCallback(() => {
    pttHeldRef.current = false;
    applyMicForMode();
  }, [applyMicForMode]);

  const cancelAssistant = useCallback(() => {
    try { convRef.current.sendUserActivity(); } catch (_) { /* ignore */ }
    dispatchSafe({ type: "ASSISTANT_STOPPED" });
  }, [dispatchSafe]);

  const endCall = useCallback(() => {
    const dur = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
    try {
      fetch(`${API}/sessions/${sessionIdRef.current}/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "user_ended", duration_ms: dur }),
        keepalive: true,
      });
    } catch (_) { /* ignore */ }
    try { convRef.current.endSession(); } catch (_) { /* ignore */ }
    connectedRef.current = false;
    startingRef.current = false;
  }, []);

  const unblockAudio = useCallback(() => { /* SDK manages playback */ }, []);

  return {
    sessionId,
    pttDown,
    pttUp,
    cancelAssistant,
    endCall,
    audioBlocked: false,
    unblockAudio,
  };
}
